/**
 * SafeHtml
 * --------
 * A minimal, audited wrapper around `dangerouslySetInnerHTML` that hard-codes
 * DOMPurify sanitization. Use this component anywhere we need to render
 * user-or-CMS-authored HTML — it makes the XSS-mitigation strategy explicit
 * and keeps every `dangerouslySetInnerHTML` usage in a single, reviewable
 * file.
 *
 * Allowed via DOMPurify config:
 *   - target / rel attributes (so external links keep noopener)
 *   - URI schemes: https?, mailto, tel, root-relative, fragment
 *
 * Strips: <script>, <iframe>, <object>, <embed>, <form>, <style>, <link>,
 *         <meta>, <base>, on*= handlers, javascript: URLs, CSS expressions, etc.
 *
 * Note (Security Sprint 3 — F-09, 2026-05-21): `<iframe>` was previously in
 * `ADD_TAGS` "for embedded video", but no CMS authoring path inserts iframes
 * today (the rich-text editor has no iframe button). Permitting iframes
 * widened the XSS surface for CMS articles authored by future agent roles —
 * removed in favour of the DOMPurify default tag allowlist, which keeps
 * standard formatting (h1-6, p, a, ul, ol, li, table, strong, em, etc.) and
 * strips the embed tag class entirely.
 */
import DOMPurify from 'dompurify';

const PURIFY_OPTS = {
  ADD_ATTR: ['target', 'rel'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|\/|#)/i,
};

export default function SafeHtml({ html, className = '', as: As = 'div', ...rest }) {
  const clean = DOMPurify.sanitize(html || '', PURIFY_OPTS);
  return (
    <As
      className={className}
      dangerouslySetInnerHTML={{ __html: clean }}
      {...rest}
    />
  );
}
